import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiIcon, BoltIcon, StarIcon, ClockIcon, XMarkIcon, ChevronDownIcon, RadioIcon } from '@heroicons/react/24/outline';
// ⭐ ADD THIS IMPORT
import { useQuery } from '@tanstack/react-query';
import { useVenues } from '../../services/venueService';
import bitcoinImage from '../../assets/images/thumbnails/bitcoin.png';
import { useTranslation } from 'react-i18next';

const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Custom hook for dropdown positioning - MUST be outside component
const useDropdownPosition = (buttonRef, isOpen) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;
    
    const updatePosition = () => {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + window.scrollY + 8, left: rect.left + window.scrollX });
    };
    
    updatePosition();
    
    const throttledUpdate = throttle(updatePosition, 100);
    window.addEventListener("resize", throttledUpdate);
    window.addEventListener("scroll", throttledUpdate, true);
    
    return () => {
      window.removeEventListener("resize", throttledUpdate);
      window.removeEventListener("scroll", throttledUpdate, true);
    };
  }, [isOpen, buttonRef]);

  return position;
};

const FilterCarousel = React.memo(({ filters, onToggleFilter, onClearAllFilters, className, venues, visibleVenues, wmWifiSlugs = new Set() }) => {
  const { t } = useTranslation();
  // ⭐ ADD THIS: React Query for venue caching
const { data: cachedVenues = [], isLoading } = useVenues({}, {
    enabled: venues.length === 0,
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000
  });
  
  // ⭐ ADD THIS: Use cached venues if not provided
  const effectiveVenues = useMemo(() => 
    venues.length > 0 ? venues : cachedVenues,
    [venues, cachedVenues]
  );

  // State for dropdown visibility
  const [dropdownStates, setDropdownStates] = useState({
    wifi: false,
    noise: false,
    budget: false,
    payment: false,
    view: false,
    rating: false,
    acFan: false,
    entryFees: false,
    breakfast: false,
    brunch: false,
    dinner: false,
    openLate: false,
    takeBooking: false,
    parking: false,
    familyFriendly: false
  });

  // ✅ ADD: Close all dropdowns when drawer interaction starts
  useEffect(() => {
    const closeAllDropdowns = () => {
      setDropdownStates({
        wifi: false,
        noise: false,
        budget: false,
        payment: false,
        view: false,
        rating: false,
        acFan: false,
        entryFees: false,
        breakfast: false,
        brunch: false,
        dinner: false,
        openLate: false,
        takeBooking: false,
        parking: false,
        familyFriendly: false
      });
    };
    
    // Listen for drawer interaction events
    const handleDrawerInteraction = () => {
      closeAllDropdowns();
    };
    
    // Add event listener for drawer interaction
    document.addEventListener('drawerInteractionStart', handleDrawerInteraction);
    
    return () => {
      document.removeEventListener('drawerInteractionStart', handleDrawerInteraction);
    };
  }, []);

  // ✅ ADD: Separate useEffect for drawer drag events
  useEffect(() => {
    const handleDrawerDrag = () => {
      setDropdownStates({
        wifi: false,
        noise: false,
        budget: false,
        payment: false,
        view: false,
        rating: false,
        acFan: false,
        entryFees: false,
        breakfast: false,
        brunch: false,
        dinner: false,
        openLate: false,
        takeBooking: false,
        parking: false,
        familyFriendly: false
      });
    };
    
    // Listen for drawer drag events
    document.addEventListener('drawerDragStart', handleDrawerDrag);
    
    return () => {
      document.removeEventListener('drawerDragStart', handleDrawerDrag);
    };
  }, []);

  // ⭐ ADD THESE REFS
  const buttonRefs = {
    wifi: useRef(null),
    noise: useRef(null),
    budget: useRef(null),
    payment: useRef(null),
    view: useRef(null),
    rating: useRef(null),
    acFan: useRef(null),
    entryFees: useRef(null),
    breakfast: useRef(null),
    brunch: useRef(null),
    dinner: useRef(null),
    openLate: useRef(null),
    takeBooking: useRef(null),
    parking: useRef(null),
    familyFriendly: useRef(null)
  };

  const dropdownRefs = {
    wifi: useRef(null),
    noise: useRef(null),
    budget: useRef(null),
    payment: useRef(null),
    view: useRef(null),
    rating: useRef(null),
    acFan: useRef(null),
    entryFees: useRef(null),
    breakfast: useRef(null),
    brunch: useRef(null),
    dinner: useRef(null),
    openLate: useRef(null),
    takeBooking: useRef(null),
    parking: useRef(null),
    familyFriendly: useRef(null)
  };

  // Memoized filter counts
// ===== REPLACE filterCounts IN FilterCarousel.jsx =====
// Location: Around line 200-300

const filterCounts = useMemo(() => {
  // ✅ Include both venues AND street vendors
  const effectiveVenuesWithVendors = effectiveVenues.filter(venue => {
    // Exclude religious venues but NOT street vendors
    if (venue.isStreetVendor) return true; // ✅ Always include vendors
    return venue.venue_category !== 'vc_religion_spiritual' && 
           venue.venue_type !== 'vt_mosque' && 
           venue.venue_category_display !== 'Religion & Spiritual';
  });

  // WiFi counts - Include vendors with payment options (they often have WiFi)
// ✅ FIXED: WiFi counts - ONLY count venues with actual WiFi (PubWifi === 1)
const wifiVenuesAll = effectiveVenuesWithVendors.filter(venue => 
  !venue.isStreetVendor && (venue.PubWifi === 1 || venue.PubWifi === true)
);
  const totalWifiVenues = wifiVenuesAll.length;
  const verifiedWifiVenues = wifiVenuesAll.filter(venue => venue.Wifi_bage === 'Verified').length;
  const unverifiedWifiVenues = totalWifiVenues - verifiedWifiVenues;
  
  const goldTierVenues = wifiVenuesAll.filter(venue => 
    venue.DL_SPeed_MBPS && parseFloat(venue.DL_SPeed_MBPS) >= 100
  ).length;
  const silverTierVenues = wifiVenuesAll.filter(venue => 
    venue.DL_SPeed_MBPS && parseFloat(venue.DL_SPeed_MBPS) >= 25 && parseFloat(venue.DL_SPeed_MBPS) < 100
  ).length;
  const bronzeTierVenues = wifiVenuesAll.filter(venue => 
    venue.DL_SPeed_MBPS && parseFloat(venue.DL_SPeed_MBPS) >= 5 && parseFloat(venue.DL_SPeed_MBPS) < 25
  ).length;

// ✅ FIXED: WiFi Speed Data count - check for actual speed test data
const wifiSpeedVenues = effectiveVenuesWithVendors.filter(venue => {
  // Check if venue has actual WiFi speed test data from backend
  const hasSpeedData = venue.hasWifiSpeedData === true || 
                       venue.hasWifiTests === true || 
                       venue.wifiTestsCount > 0;
  
  // Also verify it has valid speed values
  const hasValidSpeed = venue.DLSPeedMBPS || venue.DL_SPeed_MBPS || 
                        venue.DLSpeedMBPS || venue.dlSpeedMbps;
  
  return hasSpeedData && hasValidSpeed;
}).length;

  // Rating counts - Only venues (vendors don't have ratings yet)
  const ratingCounts = {
    3: effectiveVenuesWithVendors.filter(v => !v.isStreetVendor && v.Rating >= 3 && v.Rating < 3.5).length,
    3.5: effectiveVenuesWithVendors.filter(v => !v.isStreetVendor && v.Rating >= 3.5 && v.Rating < 4).length,
    4: effectiveVenuesWithVendors.filter(v => !v.isStreetVendor && v.Rating >= 4 && v.Rating < 4.5).length,
    4.5: effectiveVenuesWithVendors.filter(v => !v.isStreetVendor && v.Rating >= 4.5 && v.Rating < 5).length,
    5: effectiveVenuesWithVendors.filter(v => !v.isStreetVendor && v.Rating === 5).length,
    total: effectiveVenuesWithVendors.filter(v => !v.isStreetVendor && v.Rating >= 3).length
  };

  // Noise counts - Only venues (vendors don't have noise data)
  const quietVenues = effectiveVenuesWithVendors.filter(v => !v.isStreetVendor && v.Noise_Level === 'Quiet').length;
  const moderateVenues = effectiveVenuesWithVendors.filter(v => !v.isStreetVendor && v.Noise_Level === 'Moderate').length;
  const livelyVenues = effectiveVenuesWithVendors.filter(v => !v.isStreetVendor && v.Noise_Level === 'Lively').length;
  const totalNoiseVenues = quietVenues + moderateVenues + livelyVenues;

  // Budget counts - Include vendors with priceRange
  const budgetCounts = {
    1: effectiveVenuesWithVendors.filter(v => 
      v.HL_Price_Level === 1 || (v.isStreetVendor && (v.priceRange === '$' || v.BudgetFriendly === '$'))
    ).length,
    2: effectiveVenuesWithVendors.filter(v => 
      v.HL_Price_Level === 2 || (v.isStreetVendor && (v.priceRange === '$$' || v.BudgetFriendly === '$$'))
    ).length,
    3: effectiveVenuesWithVendors.filter(v => 
      v.HL_Price_Level === 3 || (v.isStreetVendor && (v.priceRange === '$$$' || v.BudgetFriendly === '$$$'))
    ).length,
    4: effectiveVenuesWithVendors.filter(v => 
      v.HL_Price_Level === 4 || (v.isStreetVendor && (v.priceRange === '$$$$' || v.BudgetFriendly === '$$$$'))
    ).length,
    total: effectiveVenuesWithVendors.filter(v => 
      (v.HL_Price_Level >= 1 && v.HL_Price_Level <= 4) || 
      (v.isStreetVendor && (v.priceRange || v.BudgetFriendly))
    ).length
  };

  // Payment counts - Include vendors
  const paymentTypes = { cash: 0, card: 0, crypto: 0 };
  effectiveVenuesWithVendors.forEach(venue => {
    // Check venue payment fields
    if (venue.Payment_Cash || (venue.isStreetVendor && venue.paymentTypes?.toLowerCase().includes('cash'))) {
      paymentTypes.cash++;
    }
    if (venue.Payment_Card || (venue.isStreetVendor && venue.paymentTypes?.toLowerCase().includes('card'))) {
      paymentTypes.card++;
    }
    if (venue.Payment_Crypto) {
      paymentTypes.crypto++;
    }
    
    // Check "Payment types" string field
    if (venue["Payment types"]) {
      const paymentString = venue["Payment types"].toString().toLowerCase();
      if (paymentString.includes('cash')) paymentTypes.cash++;
      if (paymentString.includes('card') || paymentString.includes('credit')) paymentTypes.card++;
      if (paymentString.includes('crypto') || paymentString.includes('bitcoin')) paymentTypes.crypto++;
    }
  });

  // Charging - Only venues have charging ports
  const chargingCount = effectiveVenuesWithVendors.filter(v => 
    !v.isStreetVendor && (v.Charging_Ports > 0)
  ).length;

  // WM WiFi count — venues whose slug is in the wmWifiSlugs set
  const wmWifiVenues = effectiveVenuesWithVendors.filter(venue =>
    !venue.isStreetVendor && wmWifiSlugs.has(venue.slug || '')
  ).length;

  // Return all filter counts
  return {
    wifi: {
      total: totalWifiVenues,
      verified: verifiedWifiVenues,
      unverified: unverifiedWifiVenues,
      gold: goldTierVenues,
      silver: silverTierVenues,
      bronze: bronzeTierVenues,
      speed: wifiSpeedVenues,
      wm: wmWifiVenues,
    },
    noise: { 
      total: totalNoiseVenues, 
      quiet: quietVenues, 
      moderate: moderateVenues, 
      lively: livelyVenues 
    },
    budget: budgetCounts,
    payment: { 
      total: Math.max(paymentTypes.cash, paymentTypes.card, paymentTypes.crypto), 
      ...paymentTypes 
    },
    rating: ratingCounts,
    charging: chargingCount,
    acFan: { ac: 0, fan: 0, total: 0 },
    view: { total: 0, street: 0, waterfront: 0, park: 0, ainView: 0, marinaSkyline: 0, sunset: 0, beachView: 0, courtyard: 0, souq: 0, boardwalk: 0 },
    entryFees: { yes: 0, no: 0, total: 0 },
    breakfast: { yes: 0, no: 0, total: 0 },
    brunch: { yes: 0, no: 0, total: 0 },
    openLate: { yes: 0, no: 0, total: 0 },
    dinner: { yes: 0, no: 0, total: 0 },
    takeBooking: { yes: 0, no: 0, total: 0 },
    parking: { mall: 0, valet: 0, street: 0, basement: 0, lot: 0, none: 0, total: 0 },
    familyFriendly: { score1: 0, score2: 0, score3: 0, score4: 0, score5: 0, total: 0 }
  };
}, [effectiveVenues, wmWifiSlugs]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => 
    Object.values(filters).some(value => 
      typeof value === 'boolean' ? value : (value !== '' && value !== 0)
    ), [filters]
  );

  // Optimized dropdown position calculator
  const calculatePosition = useCallback((buttonRef) => {
    if (!buttonRef.current) return { top: 0, left: 0 };
    
    const rect = buttonRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + window.scrollX
    };
  }, []);

  // Optimized ensure dropdown in view
  const ensureDropdownInView = useCallback((dropdownRef, position) => {
    if (!dropdownRef.current) return position;
    
    const dropdown = dropdownRef.current;
    const rect = dropdown.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let adjustedLeft = position.left;
    let adjustedTop = position.top;
    
    if (rect.right > viewportWidth) {
      adjustedLeft = viewportWidth - rect.width - 20;
    }
    if (rect.left < 0) {
      adjustedLeft = 20;
    }
    
    if (rect.bottom > viewportHeight) {
      adjustedTop = position.top - rect.height - 10;
    }
    
    return { top: adjustedTop, left: adjustedLeft };
  }, []);

  // Optimized scroll to keep dropdown visible
  const scrollToKeepDropdownVisible = useCallback((buttonRef, dropdownRef) => {
    if (!buttonRef.current || !dropdownRef.current) return;
    
    const container = buttonRef.current.parentElement;
    if (!container) return;
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const dropdownRect = dropdownRef.current.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    
    if (dropdownRect.right > viewportWidth || dropdownRect.left < 0) {
      const buttonOffsetLeft = buttonRect.left - containerRect.left;
      const containerWidth = container.clientWidth;
      const targetScrollLeft = buttonOffsetLeft - (containerWidth / 2) + (buttonRect.width / 2);
      
      container.scrollTo({ 
        left: Math.max(0, targetScrollLeft), 
        behavior: 'smooth' 
      });
    }
  }, []);

  // Optimized dropdown toggle handler
  const toggleDropdown = useCallback((dropdownName) => {
    setDropdownStates(prev => {
      const newState = {
        wifi: false,
        noise: false,
        budget: false,
        payment: false,
        view: false,
        rating: false,
        acFan: false,
        entryFees: false,
        breakfast: false,
        brunch: false,
        dinner: false,
        openLate: false,
        takeBooking: false,
        parking: false,
        familyFriendly: false,
        [dropdownName]: !prev[dropdownName]
      };
      return newState;
    });
  }, []);

const handleWifiFilter = useCallback((filterType = null) => {
  setDropdownStates(prev => {
    const newState = { ...prev };
    newState.wifi = false;
    return newState;
  });
  
  if (!filterType) {
    const wifiFilters = ['hasWifi', 'hasWifiUnverified', 'hasWifiGold', 'hasWifiSilver', 'hasWifiBronze', 'hasWifiSpeed', 'hasWifiWM']; // REMOVED hasWifiVerified
    wifiFilters.forEach(filter => {
      if (filters[filter]) {
        onToggleFilter(filter);
      }
    });
    return;
  }
  
  onToggleFilter(filterType);
}, [filters, onToggleFilter]);

  const handleNoiseFilter = useCallback((noiseLevel = null) => {
    // ✅ CRITICAL: Close dropdown immediately - use direct state update
    setDropdownStates(prev => {
      const newState = { ...prev };
      newState.noise = false;
      return newState;
    });
    
    if (!noiseLevel) {
      const noiseFilters = ['isQuiet', 'isModerate', 'isLively'];
      noiseFilters.forEach(filter => {
        if (filters[filter]) onToggleFilter(filter);
      });
      return;
    }
    
    onToggleFilter(noiseLevel);
  }, [filters, onToggleFilter]);

  const handleRegularFilterToggle = useCallback((filterKey) => {
    onToggleFilter(filterKey);
  }, [onToggleFilter]);

  const handlePaymentFilter = useCallback((paymentType = null) => {
    // ✅ CRITICAL: Close dropdown immediately - use direct state update
    setDropdownStates(prev => {
      const newState = { ...prev };
      newState.payment = false;
      return newState;
    });
    
    if (paymentType === null) {
      const paymentFilters = ['paymentCash', 'paymentCard', 'paymentCrypto'];
      paymentFilters.forEach(filter => {
        if (filters[filter]) onToggleFilter(filter);
      });
      return;
    }

    const filterKey = `payment${paymentType}`;
    onToggleFilter(filterKey);
  }, [filters, onToggleFilter]);

  const handleRatingFilter = useCallback((rating = null) => {
    // ✅ CRITICAL: Close dropdown immediately - use direct state update
    setDropdownStates(prev => {
      const newState = { ...prev };
      newState.rating = false;
      return newState;
    });
    
    if (rating === null) {
      const ratingFilters = ['rating3', 'rating35', 'rating4', 'rating45', 'rating5'];
      ratingFilters.forEach(filter => {
        if (filters[filter]) onToggleFilter(filter);
      });
      return;
    }

    const filterKey = `rating${rating.toString().replace('.', '')}`;
    onToggleFilter(filterKey);
  }, [filters, onToggleFilter]);

  const handleBudgetFilter = useCallback((level = null) => {
    // ✅ CRITICAL: Close dropdown immediately - use direct state update
    setDropdownStates(prev => {
      const newState = { ...prev };
      newState.budget = false;
      return newState;
    });
    
    if (level === null) {
      const budgetFilters = ['priceLevel1', 'priceLevel2', 'priceLevel3', 'priceLevel4'];
      budgetFilters.forEach(filter => {
        if (filters[filter]) onToggleFilter(filter);
      });
      return;
    }

    const filterKey = `priceLevel${level}`;
    onToggleFilter(filterKey);
  }, [filters, onToggleFilter]);

  const handleACFanFilter = useCallback((type = null) => {
    // ✅ CRITICAL: Close dropdown immediately - use direct state update
    setDropdownStates(prev => {
      const newState = { ...prev };
      newState.acFan = false;
      return newState;
    });
    
    if (type === null) {
      const acFanFilters = ['acFanAC', 'acFanFan'];
      acFanFilters.forEach(filter => {
        if (filters[filter]) onToggleFilter(filter);
      });
      return;
    }

    const filterKey = `acFan${type}`;
    onToggleFilter(filterKey);
  }, [filters, onToggleFilter]);

  const handleEntryFeesFilter = useCallback((type = null) => {
    // ✅ CRITICAL: Close dropdown immediately - use direct state update
    setDropdownStates(prev => {
      const newState = { ...prev };
      newState.entryFees = false;
      return newState;
    });
    
    if (type === null) {
      const entryFeesFilters = ['entryFeesYes', 'entryFeesNo'];
      entryFeesFilters.forEach(filter => {
        if (filters[filter]) onToggleFilter(filter);
      });
      return;
    }

    const filterKey = `entryFees${type}`;
    onToggleFilter(filterKey);
  }, [filters, onToggleFilter]);

  const handleBreakfastFilter = useCallback((type = null) => {
    // ✅ CRITICAL: Close dropdown immediately - use direct state update
    setDropdownStates(prev => {
      const newState = { ...prev };
      newState.breakfast = false;
      return newState;
    });
    
    if (type === null) {
      const breakfastFilters = ['breakfastYes', 'breakfastNo'];
      breakfastFilters.forEach(filter => {
        if (filters[filter]) onToggleFilter(filter);
      });
      return;
    }

    const filterKey = `breakfast${type}`;
    onToggleFilter(filterKey);
  }, [filters, onToggleFilter]);

  const handleBrunchFilter = useCallback((type = null) => {
    // ✅ CRITICAL: Close dropdown immediately - use direct state update
    setDropdownStates(prev => {
      const newState = { ...prev };
      newState.brunch = false;
      return newState;
    });
    
    if (type === null) {
      const brunchFilters = ['brunchYes', 'brunchNo'];
      brunchFilters.forEach(filter => {
        if (filters[filter]) onToggleFilter(filter);
      });
      return;
    }

    const filterKey = `brunch${type}`;
    onToggleFilter(filterKey);
  }, [filters, onToggleFilter]);

  const handleDinnerFilter = useCallback((type = null) => {
    // ✅ CRITICAL: Close dropdown immediately - use direct state update
    setDropdownStates(prev => {
      const newState = { ...prev };
      newState.dinner = false;
      return newState;
    });
    
    if (type === null) {
      const dinnerFilters = ['dinnerYes', 'dinnerNo'];
      dinnerFilters.forEach(filter => {
        if (filters[filter]) onToggleFilter(filter);
      });
      return;
    }

    const filterKey = `dinner${type}`;
    onToggleFilter(filterKey);
  }, [filters, onToggleFilter]);

  const handleOpenLateFilter = useCallback((type = null) => {
    // ✅ CRITICAL: Close dropdown immediately - use direct state update
    setDropdownStates(prev => {
      const newState = { ...prev };
      newState.openLate = false;
      return newState;
    });
    
    if (type === null) {
      const openLateFilters = ['openLateYes', 'openLateNo'];
      openLateFilters.forEach(filter => {
        if (filters[filter]) onToggleFilter(filter);
      });
      return;
    }

    const filterKey = `openLate${type}`;
    onToggleFilter(filterKey);
  }, [filters, onToggleFilter]);

  const handleTakeBookingFilter = useCallback((type = null) => {
    // ✅ CRITICAL: Close dropdown immediately - use direct state update
    setDropdownStates(prev => {
      const newState = { ...prev };
      newState.takeBooking = false;
      return newState;
    });
    
    if (type === null) {
      const takeBookingFilters = ['takeBookingYes', 'takeBookingNo'];
      takeBookingFilters.forEach(filter => {
        if (filters[filter]) onToggleFilter(filter);
      });
      return;
    }

    const filterKey = `takeBooking${type}`;
    onToggleFilter(filterKey);
  }, [filters, onToggleFilter]);

  const handleParkingFilter = useCallback((type = null) => {
    // ✅ CRITICAL: Close dropdown immediately - use direct state update
    setDropdownStates(prev => {
      const newState = { ...prev };
      newState.parking = false;
      return newState;
    });
    
    if (type === null) {
      const parkingFilters = ['parkingMall', 'parkingValet', 'parkingStreet', 'parkingBasement', 'parkingLot', 'parkingNone'];
      parkingFilters.forEach(filter => {
        if (filters[filter]) onToggleFilter(filter);
      });
      return;
    }

    const filterKey = `parking${type}`;
    onToggleFilter(filterKey);
  }, [filters, onToggleFilter]);

  const handleFamilyFriendlyFilter = useCallback((type = null) => {
    // ✅ CRITICAL: Close dropdown immediately - use direct state update
    setDropdownStates(prev => {
      const newState = { ...prev };
      newState.familyFriendly = false;
      return newState;
    });
    
    if (type === null) {
      const familyFilters = ['familyScore1', 'familyScore2', 'familyScore3', 'familyScore4', 'familyScore5'];
      familyFilters.forEach(filter => {
        if (filters[filter]) onToggleFilter(filter);
      });
      return;
    }

    const filterKey = `familyScore${type}`;
    onToggleFilter(filterKey);
  }, [filters, onToggleFilter]);

  const handleViewFilter = useCallback((viewType = null) => {
    // ✅ CRITICAL: Close dropdown immediately - use direct state update
    setDropdownStates(prev => {
      const newState = { ...prev };
      newState.view = false;
      return newState;
    });
    
    if (viewType === null) {
      const viewFilters = ['viewStreet', 'viewWaterfront', 'viewPark', 'viewAinView', 'viewMarinaSkyline', 'viewSunset', 'viewBeachView', 'viewCourtyard', 'viewSouq', 'viewBoardwalk'];
      viewFilters.forEach(filter => {
        if (filters[filter]) onToggleFilter(filter);
      });
      return;
    }

    const filterKey = `view${viewType.charAt(0).toUpperCase()}${viewType.slice(1)}`;
    onToggleFilter(filterKey);
  }, [filters, onToggleFilter]);

  // Memoized filter options
const filterOptions = useMemo(() => [
  {
    key: 'hasWifi',
    label: t('filters.labelWifi'),
    icon: WifiIcon,
    color: '#2563eb',
    emoji: '🛜',
    hasDropdown: true,
    count: filterCounts.wifi.total
  },
  {
    key: 'hasCharging',
    label: t('filters.labelCharging'),
    icon: BoltIcon,
    color: '#16a34a',
    emoji: '🔌',
    count: filterCounts.charging // ✅ FIX: Use charging count from filterCounts
  },
  {
    key: 'isQuiet',
    label: t('filters.labelQuietZone'),
    icon: null,
    color: '#7c3aed',
    emoji: '🔇',
    hasDropdown: true,
    count: filterCounts.noise.total
  },
  {
    key: 'rating',
    label: t('filters.labelRating'),
    icon: StarIcon,
    color: '#f59e0b',
    emoji: '⭐',
    hasDropdown: true,
    count: filterCounts.rating.total
  },
  {
    key: 'payment',
    label: t('filters.labelPaymentType'),
    icon: BoltIcon,
    color: '#16a34a',
    emoji: '💳',
    hasDropdown: true,
    count: filterCounts.payment.total
  },
  {
    key: 'budget',
    label: t('filters.budget'),
    icon: null,
    color: '#22c55e',
    emoji: '💰',
    hasDropdown: true,
    count: filterCounts.budget.total
  },
  {
    key: 'View',
    label: t('filters.labelViews'),
    icon: ClockIcon,
    color: '#8b5cf6',
    emoji: '🌅',
    hasDropdown: true,
    count: filterCounts.view.total
  },
  {
    key: 'acFan',
    label: t('filters.labelClimate'),
    icon: null,
    color: '#06b6d4',
    emoji: '❄️',
    hasDropdown: true,
    count: filterCounts.acFan.total
  },
  {
    key: 'entryFees',
    label: t('filters.labelEntryFees'),
    icon: null,
    color: '#dc2626',
    emoji: '🎫',
    hasDropdown: true,
    count: filterCounts.entryFees.total
  },
  {
    key: 'breakfast',
    label: t('filters.labelBreakfast'),
    icon: null,
    color: '#f97316',
    emoji: '🍳',
    hasDropdown: true,
    count: filterCounts.breakfast.total
  },
  {
    key: 'brunch',
    label: t('filters.labelBrunch'),
    icon: null,
    color: '#eab308',
    emoji: '🥞',
    hasDropdown: true,
    count: filterCounts.brunch.total
  },
  {
    key: 'dinner',
    label: t('filters.labelDinner'),
    icon: null,
    color: '#dc2626',
    emoji: '🍽️',
    hasDropdown: true,
    count: filterCounts.dinner.total
  },
  {
    key: 'openLate',
    label: t('filters.labelOpenLate'),
    icon: null,
    color: '#8b5cf6',
    emoji: '🌙',
    hasDropdown: true,
    count: filterCounts.openLate.total
  },
  {
    key: 'takeBooking',
    label: t('filters.labelTakeBooking'),
    icon: null,
    color: '#059669',
    emoji: '📅',
    hasDropdown: true,
    count: filterCounts.takeBooking.total
  },
  {
    key: 'parking',
    label: t('filters.parking'),
    icon: null,
    color: '#7c3aed',
    emoji: '🅿️',
    hasDropdown: true,
    count: filterCounts.parking.total
  },
  {
    key: 'familyFriendly',
    label: t('filters.labelFamilyFriendly'),
    icon: null,
    color: '#f59e0b',
    emoji: '👨‍👩‍👧‍👦',
    hasDropdown: true,
    count: filterCounts.familyFriendly.total
  },
], [filterCounts, t]);

  // Memoized filter states
const getWifiFilterState = useCallback(() => {
  if (filters.hasWifiGold) {
    return { isActive: true, label: t('filters.wifiGold'), emoji: '🥇', count: filterCounts.wifi.gold };
  } else if (filters.hasWifiSilver) {
    return { isActive: true, label: t('filters.wifiSilver'), emoji: '🥈', count: filterCounts.wifi.silver };
  } else if (filters.hasWifiBronze) {
    return { isActive: true, label: t('filters.wifiBronze'), emoji: '🥉', count: filterCounts.wifi.bronze };
  } else if (filters.hasWifiSpeed) {
    // This is correct - shows WiFi Speed Data with proper count
    return { isActive: true, label: t('filters.wifiSpeedData'), emoji: '📊', count: filterCounts.wifi.speed };
  // REMOVED: hasWifiVerified case
  } else if (filters.hasWifiUnverified) {
    return { isActive: true, label: t('filters.wifiStandard'), emoji: '📡', count: filterCounts.wifi.unverified };
  } else if (filters.hasWifiWM) {
    return { isActive: true, label: t('filters.wifiWM'), emoji: '🗺', count: filterCounts.wifi.wm };
  } else if (filters.hasWifi) {
    return { isActive: true, label: t('filters.labelWifi'), emoji: '📶', count: filterCounts.wifi.total };
  }
  return { isActive: false, label: t('filters.labelWifi'), emoji: '📶', count: filterCounts.wifi.total };
}, [filters, filterCounts.wifi, t]);

  const getNoiseFilterState = useCallback(() => {
    if (filters.isQuiet) {
      return { isActive: true, label: t('search.quiet'), emoji: '🔇', count: filterCounts.noise.quiet };
    } else if (filters.isModerate) {
      return { isActive: true, label: t('filters.noiseModerate'), emoji: '🔉', count: filterCounts.noise.moderate };
    } else if (filters.isLively) {
      return { isActive: true, label: t('filters.noiseLively'), emoji: '🔊', count: filterCounts.noise.lively };
    }
    return { isActive: false, label: t('filters.labelQuietZone'), emoji: '🔇', count: filterCounts.noise.total };
  }, [filters, filterCounts.noise, t]);

  const getRatingFilterState = useCallback(() => {
    if (filters.rating3) {
      return { isActive: true, label: '3★', emoji: '⭐', count: filterCounts.rating[3] };
    } else if (filters.rating35) {
      return { isActive: true, label: '3.5★', emoji: '⭐', count: filterCounts.rating[3.5] };
    } else if (filters.rating4) {
      return { isActive: true, label: '4★', emoji: '⭐', count: filterCounts.rating[4] };
    } else if (filters.rating45) {
      return { isActive: true, label: '4.5★', emoji: '⭐', count: filterCounts.rating[4.5] };
    } else if (filters.rating5) {
      return { isActive: true, label: '5★', emoji: '⭐', count: filterCounts.rating[5] };
    }
    return { isActive: false, label: t('filters.labelRating'), emoji: '⭐', count: filterCounts.rating.total };
  }, [filters, filterCounts.rating, t]);

  const getACFanFilterState = useCallback(() => {
    if (filters.acFanAC) {
      return { isActive: true, label: t('filters.ac'), emoji: '❄️', count: filterCounts.acFan.ac };
    } else if (filters.acFanFan) {
      return { isActive: true, label: t('filters.fan'), emoji: '🌀', count: filterCounts.acFan.fan };
    }
    return { isActive: false, label: t('filters.labelClimate'), emoji: '❄️', count: filterCounts.acFan.total };
  }, [filters, filterCounts.acFan, t]);

  const getEntryFeesFilterState = useCallback(() => {
    if (filters.entryFeesYes) {
      return { isActive: true, label: t('common.yes'), emoji: '🎫', count: filterCounts.entryFees.yes };
    } else if (filters.entryFeesNo) {
      return { isActive: true, label: t('common.no'), emoji: '🎫', count: filterCounts.entryFees.no };
    }
    return { isActive: false, label: t('filters.labelEntryFees'), emoji: '🎫', count: filterCounts.entryFees.total };
  }, [filters, filterCounts.entryFees, t]);

  const getBreakfastFilterState = useCallback(() => {
    if (filters.breakfastYes) {
      return { isActive: true, label: t('common.yes'), emoji: '🍳', count: filterCounts.breakfast.yes };
    } else if (filters.breakfastNo) {
      return { isActive: true, label: t('common.no'), emoji: '🍳', count: filterCounts.breakfast.no };
    }
    return { isActive: false, label: t('filters.labelBreakfast'), emoji: '🍳', count: filterCounts.breakfast.total };
  }, [filters, filterCounts.breakfast, t]);

  const getBrunchFilterState = useCallback(() => {
    if (filters.brunchYes) {
      return { isActive: true, label: t('common.yes'), emoji: '🥞', count: filterCounts.brunch.yes };
    } else if (filters.brunchNo) {
      return { isActive: true, label: t('common.no'), emoji: '🥞', count: filterCounts.brunch.no };
    }
    return { isActive: false, label: t('filters.labelBrunch'), emoji: '🥞', count: filterCounts.brunch.total };
  }, [filters, filterCounts.brunch, t]);

  const getDinnerFilterState = useCallback(() => {
    if (filters.dinnerYes) {
      return { isActive: true, label: t('common.yes'), emoji: '🍽️', count: filterCounts.dinner.yes };
    } else if (filters.dinnerNo) {
      return { isActive: true, label: t('common.no'), emoji: '🍽️', count: filterCounts.dinner.no };
    }
    return { isActive: false, label: t('filters.labelDinner'), emoji: '🍽️', count: filterCounts.dinner.total };
  }, [filters, filterCounts.dinner, t]);

  const getOpenLateFilterState = useCallback(() => {
    if (filters.openLateYes) {
      return { isActive: true, label: t('common.yes'), emoji: '🌙', count: filterCounts.openLate.yes };
    } else if (filters.openLateNo) {
      return { isActive: true, label: t('common.no'), emoji: '🌙', count: filterCounts.openLate.no };
    }
    return { isActive: false, label: t('filters.labelOpenLate'), emoji: '🌙', count: filterCounts.openLate.total };
  }, [filters, filterCounts.openLate, t]);

  const getTakeBookingFilterState = useCallback(() => {
    if (filters.takeBookingYes) {
      return { isActive: true, label: t('common.yes'), emoji: '📅', count: filterCounts.takeBooking.yes };
    } else if (filters.takeBookingNo) {
      return { isActive: true, label: t('common.no'), emoji: '📅', count: filterCounts.takeBooking.no };
    }
    return { isActive: false, label: t('filters.labelTakeBooking'), emoji: '📅', count: filterCounts.takeBooking.total };
  }, [filters, filterCounts.takeBooking, t]);

  const getParkingFilterState = useCallback(() => {
    if (filters.parkingMall) {
      return { isActive: true, label: t('filters.parkingMall'), emoji: '🅿️', count: filterCounts.parking.mall };
    } else if (filters.parkingValet) {
      return { isActive: true, label: t('filters.parkingValet'), emoji: '🅿️', count: filterCounts.parking.valet };
    } else if (filters.parkingStreet) {
      return { isActive: true, label: t('filters.parkingStreet'), emoji: '🅿️', count: filterCounts.parking.street };
    } else if (filters.parkingBasement) {
      return { isActive: true, label: t('filters.parkingBasement'), emoji: '🅿️', count: filterCounts.parking.basement };
    } else if (filters.parkingLot) {
      return { isActive: true, label: t('filters.parkingLot'), emoji: '🅿️', count: filterCounts.parking.lot };
    } else if (filters.parkingNone) {
      return { isActive: true, label: t('filters.parkingNone'), emoji: '🅿️', count: filterCounts.parking.none };
    }
    return { isActive: false, label: t('filters.parking'), emoji: '🅿️', count: filterCounts.parking.total };
  }, [filters, filterCounts.parking, t]);

  const getFamilyFriendlyFilterState = useCallback(() => {
    if (filters.familyScore1) {
      return { isActive: true, label: '1', emoji: '👨‍👩‍👧‍👦', count: filterCounts.familyFriendly.score1 };
    } else if (filters.familyScore2) {
      return { isActive: true, label: '2', emoji: '👨‍👩‍👧‍👦', count: filterCounts.familyFriendly.score2 };
    } else if (filters.familyScore3) {
      return { isActive: true, label: '3', emoji: '👨‍👩‍👧‍👦', count: filterCounts.familyFriendly.score3 };
    } else if (filters.familyScore4) {
      return { isActive: true, label: '4', emoji: '👨‍👩‍👧‍👦', count: filterCounts.familyFriendly.score4 };
    } else if (filters.familyScore5) {
      return { isActive: true, label: '5', emoji: '👨‍👩‍👧‍👦', count: filterCounts.familyFriendly.score5 };
    }
    return { isActive: false, label: t('filters.labelFamilyFriendly'), emoji: '👨‍👩‍👧‍👦', count: filterCounts.familyFriendly.total };
  }, [filters, filterCounts.familyFriendly, t]);

  const getBudgetFilterState = useCallback(() => {
    if (filters.priceLevel1) {
      return { isActive: true, label: '$', emoji: '💰', count: filterCounts.budget[1] };
    } else if (filters.priceLevel2) {
      return { isActive: true, label: '$$', emoji: '💰', count: filterCounts.budget[2] };
    } else if (filters.priceLevel3) {
      return { isActive: true, label: '$$$', emoji: '💰', count: filterCounts.budget[3] };
    } else if (filters.priceLevel4) {
      return { isActive: true, label: '$$$$', emoji: '💰', count: filterCounts.budget[4] };
    }
    return { isActive: false, label: t('filters.budget'), emoji: '💰', count: filterCounts.budget.total };
  }, [filters, filterCounts.budget, t]);

  const getPaymentFilterState = useCallback(() => {
    if (filters.paymentCash) {
      return { isActive: true, label: t('filters.paymentCash'), emoji: '💵', count: filterCounts.payment.cash };
    } else if (filters.paymentCard) {
      return { isActive: true, label: t('filters.paymentCard'), emoji: '💳', count: filterCounts.payment.card };
    } else if (filters.paymentCrypto) {
      return { isActive: true, label: t('filters.paymentCrypto'), emoji: '₿', count: filterCounts.payment.crypto };
    }
    return { isActive: false, label: t('filters.labelPayment'), emoji: '💳', count: filterCounts.payment.total };
  }, [filters, filterCounts.payment, t]);

  const getViewFilterState = useCallback(() => {
    const viewTypes = ['Street', 'Waterfront', 'Park', 'AinView', 'MarinaSkyline', 'Sunset', 'BeachView', 'Courtyard', 'Souq', 'Boardwalk'];

    for (const viewType of viewTypes) {
      const filterKey = `view${viewType}`;
      if (filters[filterKey]) {
        const viewKey = viewType.charAt(0).toLowerCase() + viewType.slice(1);
        return {
          isActive: true,
          label: viewType,
          emoji: '🌅',
          count: filterCounts.view[viewKey] || 0
        };
      }
    }

    return { isActive: false, label: t('filters.labelViews'), emoji: '🌅', count: filterCounts.view.total };
  }, [filters, filterCounts.view, t]);

  // Optimized click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutside = (buttonRef, dropdownRef) => 
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        buttonRef.current && !buttonRef.current.contains(event.target);

      const newDropdownStates = { ...dropdownStates };
      let shouldUpdate = false;

      if (clickedOutside(buttonRefs.wifi, dropdownRefs.wifi)) {
        newDropdownStates.wifi = false;
        shouldUpdate = true;
      }
      if (clickedOutside(buttonRefs.noise, dropdownRefs.noise)) {
        newDropdownStates.noise = false;
        shouldUpdate = true;
      }
      if (clickedOutside(buttonRefs.budget, dropdownRefs.budget)) {
        newDropdownStates.budget = false;
        shouldUpdate = true;
      }
      if (clickedOutside(buttonRefs.payment, dropdownRefs.payment)) {
        newDropdownStates.payment = false;
        shouldUpdate = true;
      }
      if (clickedOutside(buttonRefs.acFan, dropdownRefs.acFan)) {
        newDropdownStates.acFan = false;
        shouldUpdate = true;
      }
      if (clickedOutside(buttonRefs.entryFees, dropdownRefs.entryFees)) {
        newDropdownStates.entryFees = false;
        shouldUpdate = true;
      }
      if (clickedOutside(buttonRefs.breakfast, dropdownRefs.breakfast)) {
        newDropdownStates.breakfast = false;
        shouldUpdate = true;
      }
      if (clickedOutside(buttonRefs.brunch, dropdownRefs.brunch)) {
        newDropdownStates.brunch = false;
        shouldUpdate = true;
      }
      if (clickedOutside(buttonRefs.dinner, dropdownRefs.dinner)) {
        newDropdownStates.dinner = false;
        shouldUpdate = true;
      }
      if (clickedOutside(buttonRefs.openLate, dropdownRefs.openLate)) {
        newDropdownStates.openLate = false;
        shouldUpdate = true;
      }
      if (clickedOutside(buttonRefs.takeBooking, dropdownRefs.takeBooking)) {
        newDropdownStates.takeBooking = false;
        shouldUpdate = true;
      }
      if (clickedOutside(buttonRefs.parking, dropdownRefs.parking)) {
        newDropdownStates.parking = false;
        shouldUpdate = true;
      }
      if (clickedOutside(buttonRefs.familyFriendly, dropdownRefs.familyFriendly)) {
        newDropdownStates.familyFriendly = false;
        shouldUpdate = true;
      }
      if (clickedOutside(buttonRefs.view, dropdownRefs.view)) {
        newDropdownStates.view = false;
        shouldUpdate = true;
      }
      if (clickedOutside(buttonRefs.rating, dropdownRefs.rating)) {
        newDropdownStates.rating = false;
        shouldUpdate = true;
      }

      if (shouldUpdate) {
        setDropdownStates(newDropdownStates);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownStates]);

  // Base styles (memoized)
  const baseStyles = useMemo(() => ({
    card: (isActive) => ({
      width: 'clamp(50px, 12vw, 65px)',
      height: 'clamp(50px, 12vw, 65px)',
      backgroundColor: isActive ? '#fef3c7' : 'rgba(217, 100, 31, 0.05)',
      border: isActive ? '2px solid #f59e0b' : '1px solid rgba(217, 100, 31, 0.2)',
      borderRadius: 'clamp(8px, 2vw, 12px)',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0',
      boxShadow: isActive ? '0 2px 8px rgba(245, 158, 11, 0.1)' : '0 1px 4px rgba(217, 100, 31, 0.06)',
      position: 'relative',
      overflow: 'visible',
      flexShrink: 0
    }),
    iconContainer: (isActive) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 'clamp(30px, 7vw, 40px)',
      width: '100%',
      height: '100%',
      filter: isActive ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' : 'none'
    }),
    textContainer: {
      position: 'absolute',
      top: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      marginTop: 'clamp(2px, 1vw, 4px)',
      width: 'clamp(60px, 15vw, 80px)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center'
    },
    label: (isActive) => ({
      fontSize: 'clamp(7px, 2vw, 9px)',
      fontWeight: '600',
      color: isActive ? '#92400e' : '#374151',
      lineHeight: 1.1,
      wordWrap: 'break-word',
      hyphens: 'auto',
      maxWidth: '100%'
    }),
    venueCount: (isActive) => ({
      fontSize: 'clamp(6px, 1.5vw, 7px)',
      color: isActive ? '#a16207' : '#6b7280',
      fontWeight: '500',
      lineHeight: 1,
      marginTop: '1px'
    })
  }), []);

  // Dropdown Components (memoized)
  const WifiDropdownPortal = React.memo(() => {
    useEffect(() => {
      if (dropdownStates.wifi) {
        scrollToKeepDropdownVisible(buttonRefs.wifi, dropdownRefs.wifi);
      }
    }, [dropdownStates.wifi]);

    const currentPosition = useDropdownPosition(buttonRefs.wifi, dropdownStates.wifi);
    const adjustedPosition = ensureDropdownInView(dropdownRefs.wifi, currentPosition);

    if (!dropdownStates.wifi) return null;

const wifiOptions = [
  { key: 'hasWifi', emoji: '📶', count: filterCounts.wifi.total, color: '6b7280' },
  { key: 'hasWifiGold', emoji: '🥇', count: filterCounts.wifi.gold, color: 'd97706' },
  { key: 'hasWifiSilver', emoji: '🥈', count: filterCounts.wifi.silver, color: '6b7280' },
  { key: 'hasWifiBronze', emoji: '🥉', count: filterCounts.wifi.bronze, color: '92400e' },
  // WiFi Speed Data option - FIXED to show proper count
  { key: 'hasWifiSpeed', emoji: '📊', count: filterCounts.wifi.speed, color: '2563eb' },
  // REMOVED: hasWifiVerified option
  { key: 'hasWifiUnverified', emoji: '📡', count: filterCounts.wifi.unverified, color: 'd97706' },
  // WM WiFi — venues enriched from WifiMap data
  { key: 'hasWifiWM', emoji: '🗺', count: filterCounts.wifi.wm, color: 'ec4899' },
];

    return createPortal(
      <AnimatePresence>
        <motion.div
          ref={dropdownRefs.wifi}
          initial={{ opacity: 0, y: -5, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5, scale: 0.98 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          style={{
            position: 'fixed',
            top: adjustedPosition.top,
            left: adjustedPosition.left,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(245, 158, 11, 0.2)',
            minWidth: '120px',
            zIndex: 999999,
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: '6px' }}>
            {wifiOptions.map((option, index) => (
              <motion.button
                key={option.key}
                whileHover={{ backgroundColor: '#f9fafb' }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                  e.stopPropagation(); // ✅ CRITICAL: Prevent event bubbling
                  handleWifiFilter(option.key);
                }}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: filters[option.key] ? '#fef3c7' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  color: '#374151',
                  marginBottom: index < wifiOptions.length - 1 ? '2px' : '0',
                  transition: 'all 0.2s ease'
                }}
              >
                <span style={{ fontSize: '14px' }}>{option.emoji}</span>
                <span style={{ fontSize: '11px', color: option.color, fontWeight: '600' }}>
                  {option.count}
                </span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  });

  const NoiseDropdownPortal = React.memo(() => {
    useEffect(() => {
      if (dropdownStates.noise) {
        scrollToKeepDropdownVisible(buttonRefs.noise, dropdownRefs.noise);
      }
    }, [dropdownStates.noise]);

    const currentPosition = useDropdownPosition(buttonRefs.noise, dropdownStates.noise);
    const adjustedPosition = ensureDropdownInView(dropdownRefs.noise, currentPosition);

    if (!dropdownStates.noise) return null;

    const noiseOptions = [
      { key: 'isQuiet', emoji: '🔇', count: filterCounts.noise.quiet, color: '#1e40af' },
      { key: 'isModerate', emoji: '🔉', count: filterCounts.noise.moderate, color: '#d97706' },
      { key: 'isLively', emoji: '🔊', count: filterCounts.noise.lively, color: '#dc2626' }
    ];

    return createPortal(
      <AnimatePresence>
        <motion.div
          ref={dropdownRefs.noise}
          initial={{ opacity: 0, y: -5, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5, scale: 0.98 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          style={{
            position: 'fixed',
            top: adjustedPosition.top,
            left: adjustedPosition.left,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(124, 58, 237, 0.2)',
            minWidth: '120px',
            zIndex: 999999,
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: '6px' }}>
            {noiseOptions.map((option, index) => (
              <motion.button
                key={option.key}
                whileHover={{ backgroundColor: '#f9fafb' }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) =>  {
                  e.stopPropagation(); // ✅ CRITICAL: Prevent event bubbling
                  handleNoiseFilter(option.key);
                }}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: filters[option.key] ? '#f0f9ff' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  color: '#374151',
                  marginBottom: index < noiseOptions.length - 1 ? '2px' : '0',
                  transition: 'all 0.2s ease'
                }}
              >
                <span style={{ fontSize: '14px' }}>{option.emoji}</span>
                <span style={{ fontSize: '11px', color: option.color, fontWeight: '600' }}>
                  {option.count}
                </span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  });

  const BudgetDropdownPortal = React.memo(() => {
    useEffect(() => {
      if (dropdownStates.budget) {
        scrollToKeepDropdownVisible(buttonRefs.budget, dropdownRefs.budget);
      }
    }, [dropdownStates.budget]);

    const currentPosition = useDropdownPosition(buttonRefs.budget, dropdownStates.budget);
    const adjustedPosition = ensureDropdownInView(dropdownRefs.budget, currentPosition);

    if (!dropdownStates.budget) return null;

    return createPortal(
      <AnimatePresence>
        <motion.div
          ref={dropdownRefs.budget}
          initial={{ opacity: 0, y: -5, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5, scale: 0.98 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          style={{
            position: 'fixed',
            top: adjustedPosition.top,
            left: adjustedPosition.left,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(34, 197, 94, 0.2)',
            minWidth: '120px',
            maxWidth: '200px',
            zIndex: 999999,
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: '6px' }}>
            {[1, 2, 3, 4].map(level => {
              const filterKey = `priceLevel${level}`;
              const isActive = filters[filterKey];
              const dollarSigns = '$'.repeat(level);
              return (
                <motion.button
                  key={level}
                  whileHover={{ backgroundColor: '#f0fdf4' }}
                  whileTap={{ scale: 0.98 }}
                  onClick={(e) => {
                    e.stopPropagation(); // ✅ CRITICAL: Prevent event bubbling
                    handleBudgetFilter(level);
                  }}
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: isActive ? '#dcfce7' : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '12px',
                    color: '#374151',
                    marginBottom: level < 4 ? '2px' : '0',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{dollarSigns}</span>
                  <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: '600' }}>
                    {filterCounts.budget[level]}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  });

  const PaymentDropdownPortal = React.memo(() => {
    useEffect(() => {
      if (dropdownStates.payment) {
        scrollToKeepDropdownVisible(buttonRefs.payment, dropdownRefs.payment);
      }
    }, [dropdownStates.payment]);

    const currentPosition = useDropdownPosition(buttonRefs.payment, dropdownStates.payment);
    const adjustedPosition = ensureDropdownInView(dropdownRefs.payment, currentPosition);

    if (!dropdownStates.payment) return null;

    const paymentOptions = [
      { key: 'Cash', emoji: '💵', count: filterCounts.payment.cash },
      { key: 'Card', emoji: '💳', count: filterCounts.payment.card },
      { key: 'Crypto', emoji: '₿', count: filterCounts.payment.crypto, image: bitcoinImage }
    ];

    return createPortal(
      <AnimatePresence>
        <motion.div
          ref={dropdownRefs.payment}
          initial={{ opacity: 0, y: -5, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5, scale: 0.98 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          style={{
            position: 'fixed',
            top: adjustedPosition.top,
            left: adjustedPosition.left,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(22, 163, 74, 0.2)',
            minWidth: '120px',
            zIndex: 999999,
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: '6px' }}>
            {paymentOptions.map((option, index) => (
              <motion.button
                key={option.key}
                whileHover={{ backgroundColor: '#f0fdf4' }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                  e.stopPropagation(); // ✅ CRITICAL: Prevent event bubbling
                  handlePaymentFilter(option.key);
                }}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: filters[`payment${option.key}`] ? '#dcfce7' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  color: '#374151',
                  marginBottom: index < paymentOptions.length - 1 ? '2px' : '0',
                  transition: 'all 0.2s ease'
                }}
              >
                <span style={{ fontSize: '14px' }}>
                  {option.key === 'Crypto' ? (
                    <img 
                      src={option.image} 
                      alt="Bitcoin" 
                      style={{ 
                        width: '14px', 
                        height: '14px', 
                        objectFit: 'contain' 
                      }} 
                    />
                  ) : (
                    option.emoji
                  )}
                </span>
                <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: '600' }}>
                  {option.count}
                </span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  });

  const RatingDropdownPortal = React.memo(() => {
    useEffect(() => {
      if (dropdownStates.rating) {
        scrollToKeepDropdownVisible(buttonRefs.rating, dropdownRefs.rating);
      }
    }, [dropdownStates.rating]);

    const currentPosition = useDropdownPosition(buttonRefs.rating, dropdownStates.rating);
    const adjustedPosition = ensureDropdownInView(dropdownRefs.rating, currentPosition);

    if (!dropdownStates.rating) return null;

    const ratingOptions = [
      { value: 3, label: '3 Stars', emoji: '⭐⭐⭐', count: filterCounts.rating[3] },
      { value: 3.5, label: '3.5 Stars', emoji: '⭐⭐⭐✨', count: filterCounts.rating[3.5] },
      { value: 4, label: '4 Stars', emoji: '⭐⭐⭐⭐', count: filterCounts.rating[4] },
      { value: 4.5, label: '4.5 Stars', emoji: '⭐⭐⭐⭐✨', count: filterCounts.rating[4.5] },
      { value: 5, label: '5 Stars', emoji: '⭐⭐⭐⭐⭐', count: filterCounts.rating[5] }
    ];

    return createPortal(
      <AnimatePresence>
        <motion.div
          ref={dropdownRefs.rating}
          initial={{ opacity: 0, y: -5, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5, scale: 0.98 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          style={{
            position: 'fixed',
            top: adjustedPosition.top,
            left: adjustedPosition.left,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(245, 158, 11, 0.2)',
            minWidth: '140px',
            zIndex: 999999,
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: '6px' }}>
            {ratingOptions.map((rating, index) => {
              const filterKey = `rating${rating.value.toString().replace('.', '')}`;
              const isActive = filters[filterKey];
              return (
                <motion.button
                  key={rating.value}
                  whileHover={{ backgroundColor: '#fef3c7' }}
                  whileTap={{ scale: 0.98 }}
                  onClick={(e) => {
                    e.stopPropagation(); // ✅ CRITICAL: Prevent event bubbling
                    handleRatingFilter(rating.value);
                  }}
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: isActive ? '#fef3c7' : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '12px',
                    color: '#374151',
                    marginBottom: index < ratingOptions.length - 1 ? '2px' : '0',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '12px' }}>{rating.emoji}</span>
                    <span>{rating.value}+</span>
                  </div>
                  <span style={{ fontSize: '11px', color: '#f59e0b', fontWeight: '600' }}>
                    {rating.count}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  });

  const ACFanDropdownPortal = React.memo(() => {
    useEffect(() => {
      if (dropdownStates.acFan) {
        scrollToKeepDropdownVisible(buttonRefs.acFan, dropdownRefs.acFan);
      }
    }, [dropdownStates.acFan]);

    const currentPosition = useDropdownPosition(buttonRefs.acFan, dropdownStates.acFan);
    const adjustedPosition = ensureDropdownInView(dropdownRefs.acFan, currentPosition);

    if (!dropdownStates.acFan) return null;

    const acFanOptions = [
      { key: 'AC', emoji: '❄️', count: filterCounts.acFan.ac },
      { key: 'Fan', emoji: '🌀', count: filterCounts.acFan.fan }
    ];

    return createPortal(
      <AnimatePresence>
        <motion.div
          ref={dropdownRefs.acFan}
          initial={{ opacity: 0, y: -5, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5, scale: 0.98 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          style={{
            position: 'fixed',
            top: adjustedPosition.top,
            left: adjustedPosition.left,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(6, 182, 212, 0.2)',
            minWidth: '120px',
            zIndex: 999999,
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: '6px' }}>
            {acFanOptions.map((option, index) => (
              <motion.button
                key={option.key}
                whileHover={{ backgroundColor: '#ecfeff' }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                  e.stopPropagation(); // ✅ CRITICAL: Prevent event bubbling
                  handleACFanFilter(option.key);
                }}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: filters[`acFan${option.key}`] ? '#cffafe' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  color: '#374151',
                  marginBottom: index < acFanOptions.length - 1 ? '2px' : '0',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '14px' }}>{option.emoji}</span>
                  <span>{option.key === 'AC' ? t('filters.ac') : t('filters.fan')}</span>
                </div>
                <span style={{ fontSize: '11px', color: '#06b6d4', fontWeight: '600' }}>
                  {option.count}
                </span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  });

  const EntryFeesDropdownPortal = React.memo(() => {
    useEffect(() => {
      if (dropdownStates.entryFees) {
        scrollToKeepDropdownVisible(buttonRefs.entryFees, dropdownRefs.entryFees);
      }
    }, [dropdownStates.entryFees]);

    const currentPosition = useDropdownPosition(buttonRefs.entryFees, dropdownStates.entryFees);
    const adjustedPosition = ensureDropdownInView(dropdownRefs.entryFees, currentPosition);

    if (!dropdownStates.entryFees) return null;

    const entryFeesOptions = [
      { key: 'Yes', emoji: '🎫', count: filterCounts.entryFees.yes },
      { key: 'No', emoji: '🎫', count: filterCounts.entryFees.no }
    ];

    return createPortal(
      <AnimatePresence>
        <motion.div
          ref={dropdownRefs.entryFees}
          initial={{ opacity: 0, y: -5, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5, scale: 0.98 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          style={{
            position: 'fixed',
            top: adjustedPosition.top,
            left: adjustedPosition.left,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(220, 38, 38, 0.2)',
            minWidth: '120px',
            zIndex: 999999,
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: '6px' }}>
            {entryFeesOptions.map((option, index) => (
              <motion.button
                key={option.key}
                whileHover={{ backgroundColor: '#fef2f2' }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                  e.stopPropagation(); // ✅ CRITICAL: Prevent event bubbling
                  handleEntryFeesFilter(option.key);
                }}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: filters[`entryFees${option.key}`] ? '#fecaca' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  color: '#374151',
                  marginBottom: index < entryFeesOptions.length - 1 ? '2px' : '0',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '14px' }}>{option.emoji}</span>
                  <span>{t(option.key === 'Yes' ? 'common.yes' : 'common.no')}</span>
                </div>
                <span style={{ fontSize: '11px', color: '#dc2626', fontWeight: '600' }}>
                  {option.count}
                </span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  });

  const BreakfastDropdownPortal = React.memo(() => {
    useEffect(() => {
      if (dropdownStates.breakfast) {
        scrollToKeepDropdownVisible(buttonRefs.breakfast, dropdownRefs.breakfast);
      }
    }, [dropdownStates.breakfast]);

    const currentPosition = useDropdownPosition(buttonRefs.breakfast, dropdownStates.breakfast);
    const adjustedPosition = ensureDropdownInView(dropdownRefs.breakfast, currentPosition);

    if (!dropdownStates.breakfast) return null;

    const breakfastOptions = [
      { key: 'Yes', emoji: '🍳', count: filterCounts.breakfast.yes },
      { key: 'No', emoji: '🍳', count: filterCounts.breakfast.no }
    ];

    return createPortal(
      <AnimatePresence>
        <motion.div
          ref={dropdownRefs.breakfast}
          initial={{ opacity: 0, y: -5, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5, scale: 0.98 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          style={{
            position: 'fixed',
            top: adjustedPosition.top,
            left: adjustedPosition.left,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(249, 115, 22, 0.2)',
            minWidth: '120px',
            zIndex: 999999,
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: '6px' }}>
            {breakfastOptions.map((option, index) => (
              <motion.button
                key={option.key}
                whileHover={{ backgroundColor: '#fff7ed' }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                  e.stopPropagation(); // ✅ CRITICAL: Prevent event bubbling
                  handleBreakfastFilter(option.key);
                }}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: filters[`breakfast${option.key}`] ? '#fed7aa' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  color: '#374151',
                  marginBottom: index < breakfastOptions.length - 1 ? '2px' : '0',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '14px' }}>{option.emoji}</span>
                  <span>{t(option.key === 'Yes' ? 'common.yes' : 'common.no')}</span>
                </div>
                <span style={{ fontSize: '11px', color: '#f97316', fontWeight: '600' }}>
                  {option.count}
                </span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  });

  const BrunchDropdownPortal = React.memo(() => {
    useEffect(() => {
      if (dropdownStates.brunch) {
        scrollToKeepDropdownVisible(buttonRefs.brunch, dropdownRefs.brunch);
      }
    }, [dropdownStates.brunch]);

    const currentPosition = useDropdownPosition(buttonRefs.brunch, dropdownStates.brunch);
    const adjustedPosition = ensureDropdownInView(dropdownRefs.brunch, currentPosition);

    if (!dropdownStates.brunch) return null;

    const brunchOptions = [
      { key: 'Yes', emoji: '🥞', count: filterCounts.brunch.yes },
      { key: 'No', emoji: '🥞', count: filterCounts.brunch.no }
    ];

    return createPortal(
      <AnimatePresence>
        <motion.div
          ref={dropdownRefs.brunch}
          initial={{ opacity: 0, y: -5, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5, scale: 0.98 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          style={{
            position: 'fixed',
            top: adjustedPosition.top,
            left: adjustedPosition.left,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(234, 179, 8, 0.2)',
            minWidth: '120px',
            zIndex: 999999,
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: '6px' }}>
            {brunchOptions.map((option, index) => (
              <motion.button
                key={option.key}
                whileHover={{ backgroundColor: '#fefce8' }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                  e.stopPropagation(); // ✅ CRITICAL: Prevent event bubbling
                  handleBrunchFilter(option.key);
                }}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: filters[`brunch${option.key}`] ? '#fef3c7' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  color: '#374151',
                  marginBottom: index < brunchOptions.length - 1 ? '2px' : '0',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '14px' }}>{option.emoji}</span>
                  <span>{t(option.key === 'Yes' ? 'common.yes' : 'common.no')}</span>
                </div>
                <span style={{ fontSize: '11px', color: '#eab308', fontWeight: '600' }}>
                  {option.count}
                </span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  });

  const DinnerDropdownPortal = React.memo(() => {
    useEffect(() => {
      if (dropdownStates.dinner) {
        scrollToKeepDropdownVisible(buttonRefs.dinner, dropdownRefs.dinner);
      }
    }, [dropdownStates.dinner]);

    const currentPosition = useDropdownPosition(buttonRefs.dinner, dropdownStates.dinner);
    const adjustedPosition = ensureDropdownInView(dropdownRefs.dinner, currentPosition);

    if (!dropdownStates.dinner) return null;

    const dinnerOptions = [
      { key: 'Yes', emoji: '🍽️', count: filterCounts.dinner.yes },
      { key: 'No', emoji: '🍽️', count: filterCounts.dinner.no }
    ];

    return createPortal(
      <AnimatePresence>
        <motion.div
          ref={dropdownRefs.dinner}
          initial={{ opacity: 0, y: -5, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5, scale: 0.98 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          style={{
            position: 'fixed',
            top: adjustedPosition.top,
            left: adjustedPosition.left,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(220, 38, 38, 0.2)',
            minWidth: '120px',
            zIndex: 999999,
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: '6px' }}>
            {dinnerOptions.map((option, index) => (
              <motion.button
                key={option.key}
                whileHover={{ backgroundColor: '#fef2f2' }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                  e.stopPropagation(); // ✅ CRITICAL: Prevent event bubbling
                  handleDinnerFilter(option.key);
                }}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: filters[`dinner${option.key}`] ? '#fecaca' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  color: '#374151',
                  marginBottom: index < dinnerOptions.length - 1 ? '2px' : '0',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '14px' }}>{option.emoji}</span>
                  <span>{t(option.key === 'Yes' ? 'common.yes' : 'common.no')}</span>
                </div>
                <span style={{ fontSize: '11px', color: '#dc2626', fontWeight: '600' }}>
                  {option.count}
                </span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  });

  const OpenLateDropdownPortal = React.memo(() => {
    useEffect(() => {
      if (dropdownStates.openLate) {
        scrollToKeepDropdownVisible(buttonRefs.openLate, dropdownRefs.openLate);
      }
    }, [dropdownStates.openLate]);

    const currentPosition = useDropdownPosition(buttonRefs.openLate, dropdownStates.openLate);
    const adjustedPosition = ensureDropdownInView(dropdownRefs.openLate, currentPosition);

    if (!dropdownStates.openLate) return null;

    const openLateOptions = [
      { key: 'Yes', emoji: '🌙', count: filterCounts.openLate.yes },
      { key: 'No', emoji: '🌙', count: filterCounts.openLate.no }
    ];

    return createPortal(
      <AnimatePresence>
        <motion.div
          ref={dropdownRefs.openLate}
          initial={{ opacity: 0, y: -5, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5, scale: 0.98 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          style={{
            position: 'fixed',
            top: adjustedPosition.top,
            left: adjustedPosition.left,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            minWidth: '120px',
            zIndex: 999999,
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: '6px' }}>
            {openLateOptions.map((option, index) => (
              <motion.button
                key={option.key}
                whileHover={{ backgroundColor: '#faf5ff' }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                  e.stopPropagation(); // ✅ CRITICAL: Prevent event bubbling
                  handleOpenLateFilter(option.key);
                }}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: filters[`openLate${option.key}`] ? '#e9d5ff' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  color: '#374151',
                  marginBottom: index < openLateOptions.length - 1 ? '2px' : '0',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '14px' }}>{option.emoji}</span>
                  <span>{t(option.key === 'Yes' ? 'common.yes' : 'common.no')}</span>
                </div>
                <span style={{ fontSize: '11px', color: '#8b5cf6', fontWeight: '600' }}>
                  {option.count}
                </span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  });

  const TakeBookingDropdownPortal = React.memo(() => {
    useEffect(() => {
      if (dropdownStates.takeBooking) {
        scrollToKeepDropdownVisible(buttonRefs.takeBooking, dropdownRefs.takeBooking);
      }
    }, [dropdownStates.takeBooking]);

    const currentPosition = useDropdownPosition(buttonRefs.takeBooking, dropdownStates.takeBooking);
    const adjustedPosition = ensureDropdownInView(dropdownRefs.takeBooking, currentPosition);

    if (!dropdownStates.takeBooking) return null;

    const takeBookingOptions = [
      { key: 'Yes', emoji: '📅', count: filterCounts.takeBooking.yes },
      { key: 'No', emoji: '📅', count: filterCounts.takeBooking.no }
    ];

    return createPortal(
      <AnimatePresence>
        <motion.div
          ref={dropdownRefs.takeBooking}
          initial={{ opacity: 0, y: -5, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5, scale: 0.98 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          style={{
            position: 'fixed',
            top: adjustedPosition.top,
            left: adjustedPosition.left,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(5, 150, 105, 0.2)',
            minWidth: '120px',
            zIndex: 999999,
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: '6px' }}>
            {takeBookingOptions.map((option, index) => (
              <motion.button
                key={option.key}
                whileHover={{ backgroundColor: '#f0fdf4' }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                  e.stopPropagation(); // ✅ CRITICAL: Prevent event bubbling
                  handleTakeBookingFilter(option.key);
                }}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: filters[`takeBooking${option.key}`] ? '#bbf7d0' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  color: '#374151',
                  marginBottom: index < takeBookingOptions.length - 1 ? '2px' : '0',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '14px' }}>{option.emoji}</span>
                  <span>{t(option.key === 'Yes' ? 'common.yes' : 'common.no')}</span>
                </div>
                <span style={{ fontSize: '11px', color: '#059669', fontWeight: '600' }}>
                  {option.count}
                </span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  });

  const ParkingDropdownPortal = React.memo(() => {
    useEffect(() => {
      if (dropdownStates.parking) {
        scrollToKeepDropdownVisible(buttonRefs.parking, dropdownRefs.parking);
      }
    }, [dropdownStates.parking]);

    const currentPosition = useDropdownPosition(buttonRefs.parking, dropdownStates.parking);
    const adjustedPosition = ensureDropdownInView(dropdownRefs.parking, currentPosition);

    if (!dropdownStates.parking) return null;

    const parkingOptions = [
      { key: 'Mall', emoji: '🏢', count: filterCounts.parking.mall },
      { key: 'Valet', emoji: '🚗', count: filterCounts.parking.valet },
      { key: 'Street', emoji: '🛣️', count: filterCounts.parking.street },
      { key: 'Basement', emoji: '🏗️', count: filterCounts.parking.basement },
      { key: 'Lot', emoji: '🅿️', count: filterCounts.parking.lot }
    ];

    return createPortal(
      <AnimatePresence>
        <motion.div
          ref={dropdownRefs.parking}
          initial={{ opacity: 0, y: -5, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5, scale: 0.98 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          style={{
            position: 'fixed',
            top: adjustedPosition.top,
            left: adjustedPosition.left,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(124, 58, 237, 0.2)',
            minWidth: '120px',
            zIndex: 999999,
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: '6px' }}>
            {parkingOptions.map((option, index) => (
              <motion.button
                key={option.key}
                whileHover={{ backgroundColor: '#faf5ff' }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                  e.stopPropagation(); // ✅ CRITICAL: Prevent event bubbling
                  handleParkingFilter(option.key);
                }}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: filters[`parking${option.key}`] ? '#e9d5ff' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  color: '#374151',
                  marginBottom: index < parkingOptions.length - 1 ? '2px' : '0',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '14px' }}>{option.emoji}</span>
                  <span>{t(`filters.parking${option.key}`)}</span>
                </div>
                <span style={{ fontSize: '11px', color: '#7c3aed', fontWeight: '600' }}>
                  {option.count}
                </span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  });

  const FamilyFriendlyDropdownPortal = React.memo(() => {
    useEffect(() => {
      if (dropdownStates.familyFriendly) {
        scrollToKeepDropdownVisible(buttonRefs.familyFriendly, dropdownRefs.familyFriendly);
      }
    }, [dropdownStates.familyFriendly]);

    const currentPosition = useDropdownPosition(buttonRefs.familyFriendly, dropdownStates.familyFriendly);
    const adjustedPosition = ensureDropdownInView(dropdownRefs.familyFriendly, currentPosition);

    if (!dropdownStates.familyFriendly) return null;

    const familyOptions = [
      { key: '1', emoji: '⭐', count: filterCounts.familyFriendly.score1 },
      { key: '2', emoji: '⭐⭐', count: filterCounts.familyFriendly.score2 },
      { key: '3', emoji: '⭐⭐⭐', count: filterCounts.familyFriendly.score3 },
      { key: '4', emoji: '⭐⭐⭐⭐', count: filterCounts.familyFriendly.score4 },
      { key: '5', emoji: '⭐⭐⭐⭐⭐', count: filterCounts.familyFriendly.score5 }
    ];

    return createPortal(
      <AnimatePresence>
        <motion.div
          ref={dropdownRefs.familyFriendly}
          initial={{ opacity: 0, y: -5, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5, scale: 0.98 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          style={{
            position: 'fixed',
            top: adjustedPosition.top,
            left: adjustedPosition.left,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(245, 158, 11, 0.2)',
            minWidth: '120px',
            zIndex: 999999,
            overflow: 'hidden'
          }}
        >
          <div style={{ padding: '6px' }}>
            {familyOptions.map((option, index) => (
              <motion.button
                key={option.key}
                whileHover={{ backgroundColor: '#fef3c7' }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                  e.stopPropagation(); // ✅ CRITICAL: Prevent event bubbling
                  handleFamilyFriendlyFilter(option.key);
                }}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: filters[`familyScore${option.key}`] ? '#fde68a' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  color: '#374151',
                  marginBottom: index < familyOptions.length - 1 ? '2px' : '0',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '14px' }}>{option.emoji}</span>
                  <span>{option.key}</span>
                </div>
                <span style={{ fontSize: '11px', color: '#f59e0b', fontWeight: '600' }}>
                  {option.count}
                </span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  });

  const ViewDropdownPortal = React.memo(() => {
    useEffect(() => {
      if (dropdownStates.view) {
        scrollToKeepDropdownVisible(buttonRefs.view, dropdownRefs.view);
      }
    }, [dropdownStates.view]);

    const currentPosition = useDropdownPosition(buttonRefs.view, dropdownStates.view);
    const adjustedPosition = ensureDropdownInView(dropdownRefs.view, currentPosition);

    if (!dropdownStates.view) return null;

    const viewOptions = [
      { key: 'street', label: t('filters.viewStreet'), emoji: '🛣️', count: filterCounts.view.street },
      { key: 'waterfront', label: t('filters.viewWaterfront'), emoji: '🌊', count: filterCounts.view.waterfront },
      { key: 'park', label: t('filters.viewPark'), emoji: '🌳', count: filterCounts.view.park },
      { key: 'ainView', label: t('filters.viewAinView'), emoji: '🏔️', count: filterCounts.view.ainView },
      { key: 'marinaSkyline', label: t('filters.viewMarinaSkyline'), emoji: '🌆', count: filterCounts.view.marinaSkyline },
      { key: 'sunset', label: t('filters.viewSunset'), emoji: '🌅', count: filterCounts.view.sunset },
      { key: 'beachView', label: t('filters.viewBeachView'), emoji: '🏖️', count: filterCounts.view.beachView },
      { key: 'courtyard', label: t('filters.viewCourtyard'), emoji: '🏛️', count: filterCounts.view.courtyard },
      { key: 'souq', label: t('filters.viewSouq'), emoji: '🏪', count: filterCounts.view.souq },
      { key: 'boardwalk', label: t('filters.viewBoardwalk'), emoji: '🚶', count: filterCounts.view.boardwalk }
    ];

    return createPortal(
      <AnimatePresence>
        <motion.div
          ref={dropdownRefs.view}
          initial={{ opacity: 0, y: -5, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5, scale: 0.98 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          style={{
            position: 'fixed',
            top: adjustedPosition.top,
            left: adjustedPosition.left,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            minWidth: '140px',
            maxWidth: '200px',
            maxHeight: '300px',
            zIndex: 999999,
            overflow: 'hidden'
          }}
        >
          <div style={{
            padding: '6px',
            maxHeight: '280px',
            overflowY: 'auto'
          }}>
            {viewOptions.map((view, index) => {
              const filterKey = `view${view.key.charAt(0).toUpperCase() + view.key.slice(1)}`;
              const isActive = filters[filterKey];
              return (
                <motion.button
                  key={view.key}
                  whileHover={{ backgroundColor: '#f8fafc' }}
                  whileTap={{ scale: 0.98 }}
                  onClick={(e) => {
                    e.stopPropagation(); // ✅ CRITICAL: Prevent event bubbling
                    handleViewFilter(view.key);
                  }}
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: isActive ? '#f0f9ff' : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '12px',
                    color: '#374151',
                    marginBottom: index < viewOptions.length - 1 ? '2px' : '0',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '14px' }}>{view.emoji}</span>
                    <span>{view.label}</span>
                  </div>
                  <span style={{ fontSize: '11px', color: '#8b5cf6', fontWeight: '600' }}>
                    {view.count}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  });

  // Filter card renderer
  const renderFilterCard = useCallback((filter) => {
    // Special handling for WiFi filter
    if (filter.hasDropdown && filter.key === 'hasWifi') {
      const wifiState = getWifiFilterState();
      
      return (
        <div key={filter.key} style={{ position: 'relative', flexShrink: 0 }}>
          <motion.div
            ref={buttonRefs.wifi}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              if (wifiState.isActive) {
                handleWifiFilter();
              } else {
                toggleDropdown('wifi');
              }
            }}
            style={baseStyles.card(wifiState.isActive)}
          >
            <div style={baseStyles.iconContainer(wifiState.isActive)}>
              {wifiState.emoji}
            </div>

            {!wifiState.isActive && (
              <motion.div
                animate={{ rotate: dropdownStates.wifi ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  bottom: 'clamp(1px, 0.5vw, 2px)',
                  right: 'clamp(1px, 0.5vw, 2px)'
                }}
              >
                <ChevronDownIcon style={{ 
                  width: 'clamp(6px, 2vw, 8px)',
                  height: 'clamp(6px, 2vw, 8px)',
                  color: wifiState.isActive ? '#92400e' : '#6b7280'
                }} />
              </motion.div>
            )}

            <WifiDropdownPortal />
          </motion.div>

          <div style={baseStyles.textContainer}>
            <div style={baseStyles.label(wifiState.isActive)}>
              {wifiState.label}
            </div>
            <div style={baseStyles.venueCount(wifiState.isActive)}>
              {t('filters.venues', { count: wifiState.count })}
            </div>
          </div>
        </div>
      );
    }

    // Special handling for Noise Level filter
    if (filter.hasDropdown && filter.key === 'isQuiet') {
      const noiseState = getNoiseFilterState();
      
      return (
        <div key={filter.key} style={{ position: 'relative', flexShrink: 0 }}>
          <motion.div
            ref={buttonRefs.noise}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              if (noiseState.isActive) {
                handleNoiseFilter();
              } else {
                toggleDropdown('noise');
              }
            }}
            style={baseStyles.card(noiseState.isActive)}
          >
            <div style={baseStyles.iconContainer(noiseState.isActive)}>
              {noiseState.emoji}
            </div>

            {!noiseState.isActive && (
              <motion.div
                animate={{ rotate: dropdownStates.noise ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  bottom: 'clamp(1px, 0.5vw, 2px)',
                  right: 'clamp(1px, 0.5vw, 2px)'
                }}
              >
                <ChevronDownIcon style={{ 
                  width: 'clamp(6px, 2vw, 8px)',
                  height: 'clamp(6px, 2vw, 8px)',
                  color: noiseState.isActive ? '#92400e' : '#6b7280'
                }} />
              </motion.div>
            )}

            <NoiseDropdownPortal />
          </motion.div>

          <div style={baseStyles.textContainer}>
            <div style={baseStyles.label(noiseState.isActive)}>
              {noiseState.label}
            </div>
            <div style={baseStyles.venueCount(noiseState.isActive)}>
              {t('filters.venues', { count: noiseState.count })}
            </div>
          </div>
        </div>
      );
    }

    // Special handling for Rating filter
    if (filter.hasDropdown && filter.key === 'rating') {
      const ratingState = getRatingFilterState();
      
      return (
        <div key={filter.key} style={{ position: 'relative', flexShrink: 0 }}>
          <motion.div
            ref={buttonRefs.rating}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={(e) => {
              e.stopPropagation(); // ✅ CRITICAL: Prevent event bubbling
              if (ratingState.isActive) {
                handleRatingFilter();
              } else {
                toggleDropdown('rating');
              }
            }}
            style={baseStyles.card(ratingState.isActive)}
          >
            <div style={baseStyles.iconContainer(ratingState.isActive)}>
              {ratingState.emoji}
            </div>

            {!ratingState.isActive && (
              <motion.div
                animate={{ rotate: dropdownStates.rating ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  bottom: 'clamp(1px, 0.5vw, 2px)',
                  right: 'clamp(1px, 0.5vw, 2px)'
                }}
              >
                <ChevronDownIcon style={{ 
                  width: 'clamp(6px, 2vw, 8px)',
                  height: 'clamp(6px, 2vw, 8px)',
                  color: ratingState.isActive ? '#92400e' : '#6b7280'
                }} />
              </motion.div>
            )}

            <RatingDropdownPortal />
          </motion.div>

          <div style={baseStyles.textContainer}>
            <div style={baseStyles.label(ratingState.isActive)}>
              {ratingState.label}
            </div>
            <div style={baseStyles.venueCount(ratingState.isActive)}>
              {t('filters.venues', { count: ratingState.count })}
            </div>
          </div>
        </div>
      );
    }

    // Special handling for Budget filter
    if (filter.hasDropdown && filter.key === 'budget') {
      const budgetState = getBudgetFilterState();
      
      return (
        <div key={filter.key} style={{ position: 'relative', flexShrink: 0 }}>
          <motion.div
            ref={buttonRefs.budget}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              if (budgetState.isActive) {
                handleBudgetFilter();
              } else {
                toggleDropdown('budget');
              }
            }}
            style={baseStyles.card(budgetState.isActive)}
          >
            <div style={baseStyles.iconContainer(budgetState.isActive)}>
              {budgetState.emoji}
            </div>

            {!budgetState.isActive && (
              <motion.div
                animate={{ rotate: dropdownStates.budget ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  bottom: 'clamp(1px, 0.5vw, 2px)',
                  right: 'clamp(1px, 0.5vw, 2px)'
                }}
              >
                <ChevronDownIcon style={{ 
                  width: 'clamp(6px, 2vw, 8px)',
                  height: 'clamp(6px, 2vw, 8px)',
                  color: budgetState.isActive ? '#92400e' : '#6b7280'
                }} />
              </motion.div>
            )}

            <BudgetDropdownPortal />
          </motion.div>

          <div style={baseStyles.textContainer}>
            <div style={baseStyles.label(budgetState.isActive)}>
              {budgetState.label}
            </div>
            <div style={baseStyles.venueCount(budgetState.isActive)}>
              {t('filters.venues', { count: budgetState.count })}
            </div>
          </div>
        </div>
      );
    }

    // Special handling for Payment filter
    if (filter.hasDropdown && filter.key === 'payment') {
      const paymentState = getPaymentFilterState();
      
      return (
        <div key={filter.key} style={{ position: 'relative', flexShrink: 0 }}>
          <motion.div
            ref={buttonRefs.payment}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              if (paymentState.isActive) {
                handlePaymentFilter();
              } else {
                toggleDropdown('payment');
              }
            }}
            style={baseStyles.card(paymentState.isActive)}
          >
            <div style={baseStyles.iconContainer(paymentState.isActive)}>
              {/* ⭐ USE BITCOIN IMAGE FOR PAYMENT CARD */}
              <img 
                src={bitcoinImage} 
                alt="Payment" 
                style={{
                  width: 'clamp(24px, 6vw, 32px)',
                  height: 'clamp(24px, 6vw, 32px)',
                  objectFit: 'contain',
                  filter: paymentState.isActive ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' : 'none'
                }}
              />
            </div>
    
            {!paymentState.isActive && (
              <motion.div
                animate={{ rotate: dropdownStates.payment ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  bottom: 'clamp(1px, 0.5vw, 2px)',
                  right: 'clamp(1px, 0.5vw, 2px)'
                }}
              >
                <ChevronDownIcon style={{ 
                  width: 'clamp(6px, 2vw, 8px)',
                  height: 'clamp(6px, 2vw, 8px)',
                  color: paymentState.isActive ? '#92400e' : '#6b7280'
                }} />
              </motion.div>
            )}
    
            <PaymentDropdownPortal />
          </motion.div>
    
          <div style={baseStyles.textContainer}>
            <div style={baseStyles.label(paymentState.isActive)}>
              {paymentState.label}
            </div>
            <div style={baseStyles.venueCount(paymentState.isActive)}>
              {t('filters.venues', { count: paymentState.count })}
            </div>
          </div>
        </div>
      );
    }

    // Special handling for View filter
    if (filter.hasDropdown && filter.key === 'View') {
      const viewState = getViewFilterState();
      
      return (
        <div key={filter.key} style={{ position: 'relative', flexShrink: 0 }}>
          <motion.div
            ref={buttonRefs.view}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              if (viewState.isActive) {
                handleViewFilter();
              } else {
                toggleDropdown('view');
              }
            }}
            style={baseStyles.card(viewState.isActive)}
          >
            <div style={baseStyles.iconContainer(viewState.isActive)}>
              {viewState.emoji}
            </div>

            {!viewState.isActive && (
              <motion.div
                animate={{ rotate: dropdownStates.view ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  bottom: 'clamp(1px, 0.5vw, 2px)',
                  right: 'clamp(1px, 0.5vw, 2px)'
                }}
              >
                <ChevronDownIcon style={{ 
                  width: 'clamp(6px, 2vw, 8px)',
                  height: 'clamp(6px, 2vw, 8px)',
                  color: viewState.isActive ? '#92400e' : '#6b7280'
                }} />
              </motion.div>
            )}

            <ViewDropdownPortal />
          </motion.div>

          <div style={baseStyles.textContainer}>
            <div style={baseStyles.label(viewState.isActive)}>
              {viewState.label}
            </div>
            <div style={baseStyles.venueCount(viewState.isActive)}>
              {t('filters.venues', { count: viewState.count })}
            </div>
          </div>
        </div>
      );
    }

    // Special handling for AC/Fan filter
    if (filter.hasDropdown && filter.key === 'acFan') {
      const acFanState = getACFanFilterState();
      
      return (
        <div key={filter.key} style={{ position: 'relative', flexShrink: 0 }}>
          <motion.div
            ref={buttonRefs.acFan}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              if (acFanState.isActive) {
                handleACFanFilter();
              } else {
                toggleDropdown('acFan');
              }
            }}
            style={baseStyles.card(acFanState.isActive)}
          >
            <div style={baseStyles.iconContainer(acFanState.isActive)}>
              {acFanState.emoji}
            </div>

            {!acFanState.isActive && (
              <motion.div
                animate={{ rotate: dropdownStates.acFan ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  bottom: 'clamp(1px, 0.5vw, 2px)',
                  right: 'clamp(1px, 0.5vw, 2px)'
                }}
              >
                <ChevronDownIcon style={{ 
                  width: 'clamp(6px, 2vw, 8px)',
                  height: 'clamp(6px, 2vw, 8px)',
                  color: acFanState.isActive ? '#92400e' : '#6b7280'
                }} />
              </motion.div>
            )}

            <ACFanDropdownPortal />
          </motion.div>

          <div style={baseStyles.textContainer}>
            <div style={baseStyles.label(acFanState.isActive)}>
              {acFanState.label}
            </div>
            <div style={baseStyles.venueCount(acFanState.isActive)}>
              {t('filters.venues', { count: acFanState.count })}
            </div>
          </div>
        </div>
      );
    }

    // Special handling for Entry Fees filter
    if (filter.hasDropdown && filter.key === 'entryFees') {
      const entryFeesState = getEntryFeesFilterState();
      
      return (
        <div key={filter.key} style={{ position: 'relative', flexShrink: 0 }}>
          <motion.div
            ref={buttonRefs.entryFees}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              if (entryFeesState.isActive) {
                handleEntryFeesFilter();
              } else {
                toggleDropdown('entryFees');
              }
            }}
            style={baseStyles.card(entryFeesState.isActive)}
          >
            <div style={baseStyles.iconContainer(entryFeesState.isActive)}>
              {entryFeesState.emoji}
            </div>

            {!entryFeesState.isActive && (
              <motion.div
                animate={{ rotate: dropdownStates.entryFees ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  bottom: 'clamp(1px, 0.5vw, 2px)',
                  right: 'clamp(1px, 0.5vw, 2px)'
                }}
              >
                <ChevronDownIcon style={{ 
                  width: 'clamp(6px, 2vw, 8px)',
                  height: 'clamp(6px, 2vw, 8px)',
                  color: entryFeesState.isActive ? '#92400e' : '#6b7280'
                }} />
              </motion.div>
            )}

            <EntryFeesDropdownPortal />
          </motion.div>

          <div style={baseStyles.textContainer}>
            <div style={baseStyles.label(entryFeesState.isActive)}>
              {entryFeesState.label}
            </div>
            <div style={baseStyles.venueCount(entryFeesState.isActive)}>
              {t('filters.venues', { count: entryFeesState.count })}
            </div>
          </div>
        </div>
      );
    }

    // Special handling for Breakfast filter
    if (filter.hasDropdown && filter.key === 'breakfast') {
      const breakfastState = getBreakfastFilterState();
      
      return (
        <div key={filter.key} style={{ position: 'relative', flexShrink: 0 }}>
          <motion.div
            ref={buttonRefs.breakfast}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              if (breakfastState.isActive) {
                handleBreakfastFilter();
              } else {
                toggleDropdown('breakfast');
              }
            }}
            style={baseStyles.card(breakfastState.isActive)}
          >
            <div style={baseStyles.iconContainer(breakfastState.isActive)}>
              {breakfastState.emoji}
            </div>

            {!breakfastState.isActive && (
              <motion.div
                animate={{ rotate: dropdownStates.breakfast ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  bottom: 'clamp(1px, 0.5vw, 2px)',
                  right: 'clamp(1px, 0.5vw, 2px)'
                }}
              >
                <ChevronDownIcon style={{ 
                  width: 'clamp(6px, 2vw, 8px)',
                  height: 'clamp(6px, 2vw, 8px)',
                  color: breakfastState.isActive ? '#92400e' : '#6b7280'
                }} />
              </motion.div>
            )}

            <BreakfastDropdownPortal />
          </motion.div>

          <div style={baseStyles.textContainer}>
            <div style={baseStyles.label(breakfastState.isActive)}>
              {breakfastState.label}
            </div>
            <div style={baseStyles.venueCount(breakfastState.isActive)}>
              {t('filters.venues', { count: breakfastState.count })}
            </div>
          </div>
        </div>
      );
    }

    // Special handling for Brunch filter
    if (filter.hasDropdown && filter.key === 'brunch') {
      const brunchState = getBrunchFilterState();
      
      return (
        <div key={filter.key} style={{ position: 'relative', flexShrink: 0 }}>
          <motion.div
            ref={buttonRefs.brunch}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              if (brunchState.isActive) {
                handleBrunchFilter();
              } else {
                toggleDropdown('brunch');
              }
            }}
            style={baseStyles.card(brunchState.isActive)}
          >
            <div style={baseStyles.iconContainer(brunchState.isActive)}>
              {brunchState.emoji}
            </div>

            {!brunchState.isActive && (
              <motion.div
                animate={{ rotate: dropdownStates.brunch ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  bottom: 'clamp(1px, 0.5vw, 2px)',
                  right: 'clamp(1px, 0.5vw, 2px)'
                }}
              >
                <ChevronDownIcon style={{ 
                  width: 'clamp(6px, 2vw, 8px)',
                  height: 'clamp(6px, 2vw, 8px)',
                  color: brunchState.isActive ? '#92400e' : '#6b7280'
                }} />
              </motion.div>
            )}

            <BrunchDropdownPortal />
          </motion.div>

          <div style={baseStyles.textContainer}>
            <div style={baseStyles.label(brunchState.isActive)}>
              {brunchState.label}
            </div>
            <div style={baseStyles.venueCount(brunchState.isActive)}>
              {t('filters.venues', { count: brunchState.count })}
            </div>
          </div>
        </div>
      );
    }

    // Special handling for Dinner filter
    if (filter.hasDropdown && filter.key === 'dinner') {
      const dinnerState = getDinnerFilterState();
      
      return (
        <div key={filter.key} style={{ position: 'relative', flexShrink: 0 }}>
          <motion.div
            ref={buttonRefs.dinner}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              if (dinnerState.isActive) {
                handleDinnerFilter();
              } else {
                toggleDropdown('dinner');
              }
            }}
            style={baseStyles.card(dinnerState.isActive)}
          >
            <div style={baseStyles.iconContainer(dinnerState.isActive)}>
              {dinnerState.emoji}
            </div>

            {!dinnerState.isActive && (
              <motion.div
                animate={{ rotate: dropdownStates.dinner ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  bottom: 'clamp(1px, 0.5vw, 2px)',
                  right: 'clamp(1px, 0.5vw, 2px)'
                }}
              >
                <ChevronDownIcon style={{ 
                  width: 'clamp(6px, 2vw, 8px)',
                  height: 'clamp(6px, 2vw, 8px)',
                  color: dinnerState.isActive ? '#92400e' : '#6b7280'
                }} />
              </motion.div>
            )}

            <DinnerDropdownPortal />
          </motion.div>

          <div style={baseStyles.textContainer}>
            <div style={baseStyles.label(dinnerState.isActive)}>
              {dinnerState.label}
            </div>
            <div style={baseStyles.venueCount(dinnerState.isActive)}>
              {t('filters.venues', { count: dinnerState.count })}
            </div>
          </div>
        </div>
      );
    }

    // Special handling for Open Late filter
    if (filter.hasDropdown && filter.key === 'openLate') {
      const openLateState = getOpenLateFilterState();
      
      return (
        <div key={filter.key} style={{ position: 'relative', flexShrink: 0 }}>
          <motion.div
            ref={buttonRefs.openLate}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              if (openLateState.isActive) {
                handleOpenLateFilter();
              } else {
                toggleDropdown('openLate');
              }
            }}
            style={baseStyles.card(openLateState.isActive)}
          >
            <div style={baseStyles.iconContainer(openLateState.isActive)}>
              {openLateState.emoji}
            </div>

            {!openLateState.isActive && (
              <motion.div
                animate={{ rotate: dropdownStates.openLate ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  bottom: 'clamp(1px, 0.5vw, 2px)',
                  right: 'clamp(1px, 0.5vw, 2px)'
                }}
              >
                <ChevronDownIcon style={{ 
                  width: 'clamp(6px, 2vw, 8px)',
                  height: 'clamp(6px, 2vw, 8px)',
                  color: openLateState.isActive ? '#92400e' : '#6b7280'
                }} />
              </motion.div>
            )}

            <OpenLateDropdownPortal />
          </motion.div>

          <div style={baseStyles.textContainer}>
            <div style={baseStyles.label(openLateState.isActive)}>
              {openLateState.label}
            </div>
            <div style={baseStyles.venueCount(openLateState.isActive)}>
              {t('filters.venues', { count: openLateState.count })}
            </div>
          </div>
        </div>
      );
    }

    // Special handling for Take Booking filter
    if (filter.hasDropdown && filter.key === 'takeBooking') {
      const takeBookingState = getTakeBookingFilterState();
      
      return (
        <div key={filter.key} style={{ position: 'relative', flexShrink: 0 }}>
          <motion.div
            ref={buttonRefs.takeBooking}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              if (takeBookingState.isActive) {
                handleTakeBookingFilter();
              } else {
                toggleDropdown('takeBooking');
              }
            }}
            style={baseStyles.card(takeBookingState.isActive)}
          >
            <div style={baseStyles.iconContainer(takeBookingState.isActive)}>
              {takeBookingState.emoji}
            </div>

            {!takeBookingState.isActive && (
              <motion.div
                animate={{ rotate: dropdownStates.takeBooking ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  bottom: 'clamp(1px, 0.5vw, 2px)',
                  right: 'clamp(1px, 0.5vw, 2px)'
                }}
              >
                <ChevronDownIcon style={{ 
                  width: 'clamp(6px, 2vw, 8px)',
                  height: 'clamp(6px, 2vw, 8px)',
                  color: takeBookingState.isActive ? '#92400e' : '#6b7280'
                }} />
              </motion.div>
            )}

            <TakeBookingDropdownPortal />
          </motion.div>

          <div style={baseStyles.textContainer}>
            <div style={baseStyles.label(takeBookingState.isActive)}>
              {takeBookingState.label}
            </div>
            <div style={baseStyles.venueCount(takeBookingState.isActive)}>
              {t('filters.venues', { count: takeBookingState.count })}
            </div>
          </div>
        </div>
      );
    }

    // Special handling for Parking filter
    if (filter.hasDropdown && filter.key === 'parking') {
      const parkingState = getParkingFilterState();
      
      return (
        <div key={filter.key} style={{ position: 'relative', flexShrink: 0 }}>
          <motion.div
            ref={buttonRefs.parking}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              if (parkingState.isActive) {
                handleParkingFilter();
              } else {
                toggleDropdown('parking');
              }
            }}
            style={baseStyles.card(parkingState.isActive)}
          >
            <div style={baseStyles.iconContainer(parkingState.isActive)}>
              {parkingState.emoji}
            </div>

            {!parkingState.isActive && (
              <motion.div
                animate={{ rotate: dropdownStates.parking ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  bottom: 'clamp(1px, 0.5vw, 2px)',
                  right: 'clamp(1px, 0.5vw, 2px)'
                }}
              >
                <ChevronDownIcon style={{ 
                  width: 'clamp(6px, 2vw, 8px)',
                  height: 'clamp(6px, 2vw, 8px)',
                  color: parkingState.isActive ? '#92400e' : '#6b7280'
                }} />
              </motion.div>
            )}

            <ParkingDropdownPortal />
          </motion.div>

          <div style={baseStyles.textContainer}>
            <div style={baseStyles.label(parkingState.isActive)}>
              {parkingState.label}
            </div>
            <div style={baseStyles.venueCount(parkingState.isActive)}>
              {t('filters.venues', { count: parkingState.count })}
            </div>
          </div>
        </div>
      );
    }

    // Special handling for Family Friendly filter
    if (filter.hasDropdown && filter.key === 'familyFriendly') {
      const familyState = getFamilyFriendlyFilterState();
      
      return (
        <div key={filter.key} style={{ position: 'relative', flexShrink: 0 }}>
          <motion.div
            ref={buttonRefs.familyFriendly}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              if (familyState.isActive) {
                handleFamilyFriendlyFilter();
              } else {
                toggleDropdown('familyFriendly');
              }
            }}
            style={baseStyles.card(familyState.isActive)}
          >
            <div style={baseStyles.iconContainer(familyState.isActive)}>
              {familyState.emoji}
            </div>

            {!familyState.isActive && (
              <motion.div
                animate={{ rotate: dropdownStates.familyFriendly ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  bottom: 'clamp(1px, 0.5vw, 2px)',
                  right: 'clamp(1px, 0.5vw, 2px)'
                }}
              >
                <ChevronDownIcon style={{ 
                  width: 'clamp(6px, 2vw, 8px)',
                  height: 'clamp(6px, 2vw, 8px)',
                  color: familyState.isActive ? '#92400e' : '#6b7280'
                }} />
              </motion.div>
            )}

            <FamilyFriendlyDropdownPortal />
          </motion.div>

          <div style={baseStyles.textContainer}>
            <div style={baseStyles.label(familyState.isActive)}>
              {familyState.label}
            </div>
            <div style={baseStyles.venueCount(familyState.isActive)}>
              {t('filters.venues', { count: familyState.count })}
            </div>
          </div>
        </div>
      );
    }

    // Regular filter cards with toggle/untoggle functionality
    const isActive = filters[filter.key];

    return (
      <div key={filter.key} style={{ position: 'relative', flexShrink: 0 }}>
        <motion.div
          whileHover={{ scale: 1.02, y: -1 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleRegularFilterToggle(filter.key)}
          style={baseStyles.card(isActive)}
        >
          <div style={baseStyles.iconContainer(isActive)}>
            {filter.emoji}
          </div>
        </motion.div>

        <div style={baseStyles.textContainer}>
          <div style={baseStyles.label(isActive)}>
            {filter.label}
          </div>
          <div style={baseStyles.venueCount(isActive)}>
            {t('filters.venues', { count: filter.count })}
          </div>
        </div>
      </div>
    );
  }, [
    filters, 
    baseStyles, 
    dropdownStates, 
    getWifiFilterState, 
    getNoiseFilterState, 
    getRatingFilterState, 
    getBudgetFilterState, 
    getPaymentFilterState, 
    getViewFilterState, 
    getACFanFilterState,
    handleWifiFilter,
    handleNoiseFilter,
    handleRatingFilter,
    handleBudgetFilter,
    handlePaymentFilter,
    handleViewFilter,
    handleACFanFilter,
    handleRegularFilterToggle,
    toggleDropdown
  ]);

   // ⭐ LOADING CHECK - Must be after ALL hooks
  if (isLoading && effectiveVenues.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          style={{
            width: '32px',
            height: '32px',
            border: '3px solid #f3f4f6',
            borderTop: '3px solid #ea580c',
            borderRadius: '50%',
            margin: '0 auto'
          }}
        />
      </div>
    );
  }
  
  return (
    <div className={`filter-carousel ${className}`} style={{ marginBottom: '16px', position: 'relative' }}>
      <div 
        className="filter-cards-container"
        style={{
          display: 'flex',
          gap: 'clamp(8px, 2vw, 12px)',
          overflowX: 'auto',
          padding: '8px',
          paddingBottom: 'clamp(24px, 5vw, 32px)',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          position: 'relative',
          zIndex: 1
        }}
      >
        <AnimatePresence>
        {hasActiveFilters && (
  <motion.div
    initial={{ opacity: 0, scale: 0.8, x: -20 }}
    animate={{ opacity: 1, scale: 1, x: 0 }}
    exit={{ opacity: 0, scale: 0.8, x: -20 }}
    transition={{ duration: 0.3, ease: "easeOut" }}
    style={{ flexShrink: 0 }}
  >
    <motion.button
      whileHover={{ scale: 1.05, y: -2 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClearAllFilters}
      style={baseStyles.card(true)}
    >
      <div style={baseStyles.iconContainer(true)}>
        <div style={{
          width: 'clamp(20px, 5vw, 26px)',
          height: 'clamp(20px, 5vw, 26px)',
          borderRadius: '50%',
          border: 'none',
          backgroundColor: 'black',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          transition: 'all 0.3s ease'
        }}>
          <div style={{
            width: 'clamp(6px, 1.5vw, 8px)',
            height: 'clamp(6px, 1.5vw, 8px)',
            borderRadius: '50%',
            backgroundColor: 'white',
            transition: 'all 0.3s ease'
          }} />
        </div>
      </div>
      <div style={baseStyles.textContainer}>
        <div style={{
          ...baseStyles.label(true),
          color: '#374151'
        }}>
          {t('filters.clearAll')}
        </div>
      </div>
    </motion.button>
  </motion.div>
)}
        </AnimatePresence>

        {filterOptions.map(renderFilterCard)}
      </div>

      <style>{`
        .filter-cards-container::-webkit-scrollbar {
          height: 3px;
        }
        
        .filter-cards-container::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 2px;
        }
        
        .filter-cards-container::-webkit-scrollbar-thumb {
          background: #f59e0b;
          border-radius: 2px;
        }
        
        .filter-cards-container::-webkit-scrollbar-thumb:hover {
          background: #d97706;
        }

        .filter-cards-container {
          scrollbar-width: thin;
          scrollbar-color: #f59e0b #f1f5f9;
        }
      `}</style>
    </div>
  );
});

export default FilterCarousel;